import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Wine } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BASE_URL}/api${url}`;
  return url;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [headerSettings, setHeaderSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setHeaderSettings(response.data?.header);
      } catch (error) {
        console.error("Error fetching header settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Default navigation links - can be overridden by CMS
  const defaultNavLinks = [
    { path: "/", label: "Home" },
    { path: "/experiences", label: "Experiences" },
    { path: "/treats", label: "Treats" },
    { path: "/events", label: "Events" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact" }
  ];

  const navLinks = headerSettings?.nav_links || defaultNavLinks;
  const bookNowLink = headerSettings?.book_now || { label: "Book Now", url: "/booking" };
  const logo = headerSettings?.logo || { text: "MIX & MINGLE", highlight: "Events" };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="navbar">
      <div className="container-luxury">
        <div className="flex items-center justify-between h-20">
          {/* Logo - CMS Editable */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link">
            {logo.image ? (
              <img 
                src={getImageUrl(logo.image)} 
                alt={logo.text} 
                className="h-10 w-auto"
              />
            ) : (
              <>
                <Wine className="w-8 h-8 text-[#D4AF37] transition-transform duration-300 group-hover:rotate-12" />
                <span className="font-['Playfair_Display'] text-xl font-semibold tracking-tight">
                  <span className="text-[#F5F5F0]">{logo.text} </span>
                  <span className="text-[#D4AF37]">{logo.highlight}</span>
                </span>
              </>
            )}
          </Link>

          {/* Desktop Navigation - CMS Editable */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link, index) =>
              <Link
                key={index}
                to={link.path || link.url}
                className={`nav-link ${isActive(link.path || link.url) ? "active" : ""}`}
                data-testid={`nav-${link.label.toLowerCase()}`}>
                {link.label}
              </Link>
            )}
          </div>

          {/* Book Now Button - Desktop - CMS Editable */}
          <Link
            to={bookNowLink.url}
            className="hidden md:block btn-primary text-sm"
            data-testid="nav-book-now">
            {bookNowLink.label}
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-[#F5F5F0]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen &&
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="mobile-menu open"
            data-testid="mobile-menu">
            <div className="flex flex-col gap-8">
              {navLinks.map((link, index) =>
                <Link
                  key={index}
                  to={link.path || link.url}
                  className={`text-2xl font-['Playfair_Display'] transition-colors ${
                    isActive(link.path || link.url) ? "text-[#D4AF37]" : "text-[#F5F5F0] hover:text-[#D4AF37]"
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                  {link.label}
                </Link>
              )}
              <Link
                to={bookNowLink.url}
                className="btn-primary text-center mt-4"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-book-now">
                {bookNowLink.label}
              </Link>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;